import { useEffect, useRef } from 'react';

import { stripLeadingOrdinal } from '../../config/content-types';
import { useDismissable } from '../../layout/useDismissable';
import type { LectureLibrarySelection } from './LectureLibraryTree';

export interface UseLectureConfirmProps {
  selection: LectureLibrarySelection | null;
  onClose: () => void;
  onConfirm: (selection: LectureLibrarySelection) => void;
}

// its own layer rather than components/Modal - the refreshed dialog has different metrics and a blurred scrim
export function UseLectureConfirm({ selection, onClose, onConfirm }: UseLectureConfirmProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const open = selection !== null;
  // Escape and tap-outside both live in the hook; this effect keeps the focus trap only
  const dismiss = useDismissable(open, onClose);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (confirmRef.current) confirmRef.current.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const matches = dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled])');
      const focusable = Array.prototype.slice.call(matches) as HTMLElement[];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown, false);
    return () => {
      document.removeEventListener('keydown', onKeyDown, false);
      if (previous && document.body.contains(previous)) previous.focus();
    };
  }, [open]);

  if (!selection) return null;
  const name = stripLeadingOrdinal(selection.unit.name);

  return (
    <div className="use-lecture-layer" role="presentation" {...dismiss}>
      <div className="use-lecture" role="dialog" aria-modal={true} aria-labelledby="use-lecture-title" ref={dialogRef}>
        <header className="use-lecture__head">
          <h2 id="use-lecture-title">Sử dụng bài giảng</h2>
          <button type="button" className="use-lecture__close" aria-label="Đóng" onClick={onClose}>
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" aria-hidden={true}><path d="M3.5 3.5l8 8M11.5 3.5l-8 8" /></svg>
          </button>
        </header>
        <div className="use-lecture__body">
          <p>Bạn có muốn sử dụng <strong>{name}</strong> cho tiết học hiện tại không?</p>
        </div>
        <footer className="use-lecture__foot">
          <button type="button" className="use-lecture__skip" onClick={onClose}>Bỏ qua</button>
          <button type="button" className="use-lecture__confirm" ref={confirmRef} onClick={() => onConfirm(selection)}>Xác nhận</button>
        </footer>
      </div>
    </div>
  );
}
